import { motion } from "framer-motion";
import { SectionLabel } from "./SectionLabel";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";

const steps = ["Track", "Understand", "Adjust", "Repeat"];

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
};

export const HabitLoop = () => {
  const reduced = usePrefersReducedMotion();

  return (
    <section className="py-24 md:py-32 lg:py-40 bg-white/50" data-testid="habit-loop-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <SectionLabel number={6} title="The Habit Loop" />

        <motion.h2
          {...fadeUp}
          className="font-heading text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight font-medium text-[#171717] max-w-[720px]"
        >
          Small actions. Better understanding. Stronger habits.
        </motion.h2>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="relative mt-16 md:mt-20"
          data-testid="habit-loop-diagram"
        >
          <div className="hidden md:block absolute top-6 left-[12.5%] right-[12.5%] h-px bg-[#171717]/12" />
          {!reduced && (
            <motion.span
              className="hidden md:block absolute top-[19px] h-2 w-2 rounded-full bg-[#176B4D]"
              animate={{ left: ["12.5%", "37.5%", "62.5%", "87.5%", "12.5%"] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            />
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-6">
            {steps.map((step, i) => (
              <div key={step} className="flex flex-col items-center text-center" data-testid={`habit-step-${step.toLowerCase()}`}>
                <div className="h-3 w-3 rounded-full bg-[#171717] mb-5 relative z-10" />
                <span className="font-heading text-xl md:text-2xl font-medium text-[#171717]">
                  {step}
                </span>
                <span className="mt-1 text-xs text-[#171717]/40 font-mono">0{i + 1}</span>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.p
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.25 }}
          className="mt-16 md:mt-20 text-xl md:text-2xl text-[#171717]/60 max-w-[560px]"
        >
          Quantify isn&rsquo;t trying to make you perfect. It&rsquo;s helping
          you pay attention long enough for better habits to stick.
        </motion.p>
      </div>
    </section>
  );
};
